import { useState, useEffect } from "react";
import PortalLayout from "@/pages/portal/components/PortalLayout";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetAuth } from "@/services/http";
import { isApiConfigured } from "@/services/http";
import ListingPerformance from "./components/ListingPerformance";
import PremiumFeaturesCard from "./components/PremiumFeaturesCard";
import VideoTourNudge from "./components/VideoTourNudge";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Lead {
  id:         number;
  formType:   "enquiry" | "listing" | "contact";
  name:       string;
  email:      string | null;
  phone:      string | null;
  subject:    string | null;
  message:    string;
  isRead:     boolean;
  createdAt:  string;
  sourcePage: string | null;
}

// ── Demo data ─────────────────────────────────────────────────────────────────

const DEMO_LEADS: Lead[] = [
  { id: 1,  formType: "enquiry", name: "Arun Shetty",     email: "arun@example.com",  phone: "+91 98440 11234", subject: "Menu enquiry",        message: "Hi, do you have special packages for corporate lunch? We have a team of 25.",      isRead: false, createdAt: "2026-08-15T10:32:00Z", sourcePage: "/business/mangalore-harbour-fish-house" },
  { id: 2,  formType: "enquiry", name: "Priya Kamath",    email: "priya@example.com", phone: "+91 97410 55678", subject: "Booking enquiry",     message: "Can I book a table for 4 on Saturday evening? Please confirm availability.",          isRead: false, createdAt: "2026-08-14T16:10:00Z", sourcePage: "/business/mangalore-harbour-fish-house" },
  { id: 3,  formType: "enquiry", name: "Vijay Nayak",     email: null,                phone: "+91 94489 90001", subject: "Price enquiry",       message: "What are your rates for Prawn Gassi this week? Asking for a family gathering.",        isRead: true,  createdAt: "2026-08-13T09:45:00Z", sourcePage: "/business/mangalore-harbour-fish-house" },
  { id: 4,  formType: "enquiry", name: "Reshma D'Souza",  email: "reshma@mail.in",    phone: "+91 90088 44312", subject: null,                  message: "Interested in your lunch thali. What is the timing and is it available on Sundays?",   isRead: true,  createdAt: "2026-08-12T13:21:00Z", sourcePage: "/business/mangalore-harbour-fish-house" },
  { id: 5,  formType: "listing", name: "Suresh Bhat",     email: "sbhat@bizz.in",     phone: "+91 98451 77890", subject: "New listing",         message: "I want to list my bakery in Udupi. Business plan sounds right for us.",               isRead: false, createdAt: "2026-08-11T08:05:00Z", sourcePage: "/list-your-business" },
  { id: 6,  formType: "enquiry", name: "Lakshmi Hegde",   email: "lakshmi@net.in",    phone: null,              subject: "Catering query",      message: "Do you do catering for weddings? We have an event in September near Mangalore.",      isRead: true,  createdAt: "2026-08-10T14:30:00Z", sourcePage: "/business/mangalore-harbour-fish-house" },
  { id: 7,  formType: "contact", name: "Rajan Mallya",    email: "rajan@mail.com",    phone: "+91 96321 88765", subject: "Partnership query",   message: "Would your platform be open to featuring local food bloggers? Let\'s connect.",       isRead: true,  createdAt: "2026-08-09T11:55:00Z", sourcePage: "/contact" },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<Lead["formType"], string> = {
  enquiry: "Enquiry",
  listing: "Listing",
  contact: "Contact",
};

const TYPE_COLORS: Record<Lead["formType"], string> = {
  enquiry: "bg-primary-100 text-primary-700",
  listing: "bg-accent-100 text-accent-700",
  contact: "bg-secondary-100 text-secondary-700",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function PortalDashboardPage() {
  const { user, accessToken, isDemoMode } = useAuth();
  const [leads,     setLeads]     = useState<Lead[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [tab,       setTab]       = useState<"all" | Lead["formType"] | "unread">("all");
  const [expandId,  setExpandId]  = useState<number | null>(null);

  useEffect(() => {
    if (!isApiConfigured() || !accessToken) {
      setTimeout(() => { setLeads(DEMO_LEADS); setLoading(false); }, 400);
      return;
    }
    setLoading(true);
    setError("");
    apiGetAuth<{ data: Lead[] }>("/leads", accessToken)
      .then((res) => setLeads(res.data ?? []))
      .catch((err: Error) => {
        setError(err.message ?? "We could not load your leads. Please try again.");
        setLeads([]);
      })
      .finally(() => setLoading(false));
  }, [accessToken, reloadToken]);

  // Stats
  const total    = leads.length;
  const unread   = leads.filter((l) => !l.isRead).length;
  const thisWeek = leads.filter((l) => {
    const d = new Date(l.createdAt);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    return diff < 7 * 24 * 60 * 60 * 1000;
  }).length;

  // Filter
  const filtered = leads.filter((l) => {
    if (tab === "all")    return true;
    if (tab === "unread") return !l.isRead;
    return l.formType === tab;
  });

  const stats = [
    { label: "Total Leads",  value: total,    icon: "ri-mail-open-line",       color: "text-primary-600",   bg: "bg-primary-50"   },
    { label: "Unread",       value: unread,   icon: "ri-mail-unread-line",      color: "text-rose-600",      bg: "bg-rose-50"      },
    { label: "This Week",    value: thisWeek, icon: "ri-calendar-check-line",   color: "text-accent-600",    bg: "bg-accent-50"    },
    { label: "Response Rate",value: total ? `${Math.round(((total - unread) / total) * 100)}%` : "—",
      icon: "ri-line-chart-line", color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  const tabs: { key: typeof tab; label: string }[] = [
    { key: "all",     label: `All (${total})` },
    { key: "unread",  label: `Unread (${unread})` },
    { key: "enquiry", label: "Enquiries" },
    { key: "listing", label: "Listings" },
    { key: "contact", label: "Contact" },
  ];

  return (
    <PortalLayout>
      <div className="max-w-5xl">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">
              Welcome back, {user?.name.split(" ")[0]} 👋
            </h1>
            <p className="mt-1 text-sm text-foreground-600">
              Here's your lead inbox and business activity summary.
            </p>
          </div>
          {isDemoMode && (
            <span className="shrink-0 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
              Demo Mode
            </span>
          )}
        </div>

        {error && (
          <div className="mb-6 flex items-start justify-between gap-3 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <span className="flex items-start gap-2.5">
              <i className="ri-error-warning-line mt-0.5 shrink-0 text-base" />
              {error}
            </span>
            <button
              type="button"
              onClick={() => setReloadToken((t) => t + 1)}
              className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
            >
              Retry
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-xl border border-background-200 bg-background-50 p-5">
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${s.bg}`}>
                <i className={`${s.icon} text-lg ${s.color}`} />
              </div>
              <p className="font-heading text-2xl font-bold text-foreground-950">{loading ? "—" : s.value}</p>
              <p className="mt-0.5 text-xs text-foreground-500">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Premium video tour nudge */}
        <div className="mb-4">
          <VideoTourNudge />
        </div>

        {/* Premium features */}
        <div className="mb-8">
          <PremiumFeaturesCard />
        </div>

        {/* Listing performance */}
        <div className="mb-8">
          <ListingPerformance />
        </div>

        {/* Lead Inbox */}
        <div className="rounded-xl border border-background-200 bg-background-50">
          <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
            <h2 className="font-heading text-base font-bold text-foreground-950">Lead Inbox</h2>
            <span className="text-xs text-foreground-500">
              {filtered.length} lead{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 overflow-x-auto border-b border-background-200 px-5 pt-3 pb-0">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`cursor-pointer whitespace-nowrap border-b-2 px-3 pb-2.5 text-xs font-semibold transition-colors ${
                  tab === t.key
                    ? "border-primary-500 text-primary-700"
                    : "border-transparent text-foreground-500 hover:text-foreground-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Lead list */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400" />
              </div>
              <p className="text-sm font-medium text-foreground-700">No leads here yet</p>
              <p className="mt-1 text-xs text-foreground-500">Leads will appear here once customers submit enquiries.</p>
            </div>
          ) : (
            <div className="divide-y divide-background-100">
              {filtered.map((lead) => (
                <div key={lead.id} className={`transition-colors ${!lead.isRead ? "bg-primary-50/40" : ""}`}>
                  <button
                    type="button"
                    onClick={() => setExpandId(expandId === lead.id ? null : lead.id)}
                    className="flex w-full cursor-pointer items-start gap-4 px-6 py-4 text-left hover:bg-background-100"
                  >
                    {/* Avatar */}
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                      {lead.name[0].toUpperCase()}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className={`text-sm font-semibold ${!lead.isRead ? "text-foreground-950" : "text-foreground-700"}`}>
                          {lead.name}
                        </p>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TYPE_COLORS[lead.formType]}`}>
                          {TYPE_LABELS[lead.formType]}
                        </span>
                        {!lead.isRead && (
                          <span className="h-2 w-2 rounded-full bg-primary-500" />
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-xs text-foreground-500">
                        {lead.subject ?? lead.message.slice(0, 60)}
                        {!lead.subject && lead.message.length > 60 ? "…" : ""}
                      </p>
                    </div>

                    {/* Meta */}
                    <div className="flex shrink-0 flex-col items-end gap-1.5">
                      <p className="text-[11px] text-foreground-400">{timeAgo(lead.createdAt)}</p>
                      <i className={`text-foreground-400 text-sm ${expandId === lead.id ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {expandId === lead.id && (
                    <div className="mx-6 mb-4 rounded-lg border border-background-200 bg-background-50 p-5">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2 text-sm">
                          <p className="flex items-center gap-2 text-foreground-700">
                            <i className="ri-user-3-line text-foreground-400 text-base" />
                            <span className="font-medium">{lead.name}</span>
                          </p>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-primary-600 hover:underline">
                              <i className="ri-mail-line text-foreground-400 text-base" />
                              {lead.email}
                            </a>
                          )}
                          {lead.phone && (
                            <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-primary-600 hover:underline">
                              <i className="ri-phone-line text-foreground-400 text-base" />
                              {lead.phone}
                            </a>
                          )}
                        </div>
                        <div className="space-y-1 text-xs text-foreground-500">
                          <p>Received: {new Date(lead.createdAt).toLocaleString("en-IN")}</p>
                          {lead.sourcePage && <p>Page: {lead.sourcePage}</p>}
                        </div>
                      </div>
                      <div className="mt-4 rounded-lg bg-background-100 px-4 py-3 text-sm text-foreground-700">
                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground-500">Message</p>
                        {lead.message}
                      </div>
                      <div className="mt-4 flex gap-2">
                        {lead.email && (
                          <a href={`mailto:${lead.email}?subject=Re: ${lead.subject ?? "Your enquiry"}`}
                            className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap">
                            <i className="ri-reply-line text-sm" /> Reply by email
                          </a>
                        )}
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`}
                            className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap">
                            <i className="ri-phone-line text-sm" /> Call
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}